import { Box, Stack } from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';

interface DeathOrderSelectorProps {
  /** The ID of the primary client who is currently selected to die first */
  selectedFirstDeathClientId: string;
  /** Callback when the death order is changed */
  onDeathOrderChange: (newFirstDeathClientId: string) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /**
   * Optional function to render a badge for each death order option.
   * Receives the client ID and whether the item is selected, returns a ReactNode.
   */
  renderBadge?: (args: { clientId: string; isSelected: boolean }) => ReactNode;
}

export function DeathOrderSelector({
  selectedFirstDeathClientId,
  onDeathOrderChange,
  disabled,
  renderBadge,
}: DeathOrderSelectorProps) {
  const { primaryClients, isTwoClientHousehold } = useHouseholdDetailsContext();

  // Find the selected client, defaulting to the primary client if no valid selection
  const selectedClient = useMemo(() => {
    if (!primaryClients) return undefined;

    // Try to find the client matching the provided ID
    const matchingClient = primaryClients.find(
      (c) => c.id === selectedFirstDeathClientId
    );
    if (matchingClient) return matchingClient;

    // Fall back to the first client
    return primaryClients[0];
  }, [primaryClients, selectedFirstDeathClientId]);

  // Use the effective selected client ID for menu item highlighting
  const effectiveSelectedId = selectedClient?.id;

  const menuItems = useMemo(() => {
    if (!primaryClients) return [];

    return primaryClients.map((client, idx) => {
      const isSelected = client.id === effectiveSelectedId;

      return {
        id: client.id,
        label: `${client.displayGivenName} dies first`,
        subtext: idx === 0 ? 'Primary death order' : 'Secondary death order',
        isSelected,
        onClick: () => {
          if (!isSelected) {
            onDeathOrderChange(client.id);
          }
        },
      };
    });
  }, [primaryClients, effectiveSelectedId, onDeathOrderChange]);

  // Don't render if we have no client data at all
  if (!selectedClient) {
    return null;
  }

  // For single-client households, show the selector but disabled
  const isSingleClientHousehold = !isTwoClientHousehold;

  return (
    <ButtonWithPopover
      label={`Death order: ${selectedClient.displayGivenName} dies first`}
      size="sm"
      variant="secondary"
      popperVariant="menuBelow"
      disabled={disabled || isSingleClientHousehold}
      data-testid="DeathOrderSelector"
    >
      <Box minWidth={250}>
        {menuItems.map((item) => (
          <MenuItem
            key={item.id}
            label={item.label}
            subtext={item.subtext}
            onClick={item.onClick}
            iconAfter={
              <Stack direction="row" alignItems="center" spacing={1}>
                {renderBadge?.({
                  clientId: item.id,
                  isSelected: item.isSelected,
                })}
                {item.isSelected && (
                  <CheckIcon size={16} color={COLORS.TEXT.PRIMARY} />
                )}
              </Stack>
            }
          />
        ))}
      </Box>
    </ButtonWithPopover>
  );
}
